import { cn } from "@/utils/theme";
import type { FormPage } from "@/workflow/components/configurations/form/types";
import { VariableKeyBox } from "../key-box";
import { Avatar, Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { useState } from "react";
import { parseType } from "@/workflow_api/hooks/variables/useGetVariables";

interface Props {
  pages: FormPage[];
  display_name?: string;
}

export const FormOutputVariabels = ({ pages, display_name }: Props) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="w-full pr-4 pt-8">
      <div
        className={cn(
          "pl-2.5 space-x-2 flex items-center justify-start",
          isOpen ? "pb-0" : "pb-8",
        )}
      >
        <div className="bg-transparent p-1 border s-neutral-300 rounded-[12px]">
          <Avatar
            size="2xs"
            shape={null}
            className={`items-center justify-center border rounded-lg bg-surface-action-accent-quiet border-action-quiet text-action-quiet`}
          >
            <div className="">
              <Icon type="fas" name="bars" size="sm" className="" />
            </div>
          </Avatar>
        </div>
        <div className="flex w-full items-center space-x-2 min-w-0">
          <Badge size="sm" variant="action">
            Formulario
          </Badge>

          <div className="flex-1 font-semibold text-body-loud truncate">
            {display_name}
          </div>

          <Button
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle"
            size="icon"
            variant="ghost"
            className={`ml-auto mr-3 ${isOpen ? "rotate-0" : "rotate-180"}`}
          >
            <Icon name="angle-up" type="fas" />
          </Button>
        </div>
      </div>

      <div className="pr-4">
        {pages.map((page) => {
          const isLast = pages.indexOf(page) === pages.length - 1;
          const index = pages.indexOf(page) + 1;
          return (
            <div>
              <div
                id="content"
                className={`
        relative overflow-hidden transition-[max-height] duration-200 ease-in-out ${isOpen ? "min-h-full " : "max-h-0"}
        `}
              >
                <InputOutputBlock
                  form={page}
                  isLast={isLast}
                  index={index}
                  key={`form-page-${index}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const InputOutputBlock = ({
  form,
  index,
  isLast,
}: {
  form: FormPage;
  index: number;
  isLast: boolean;
}) => {
  return (
    <div className="flex">
      <div
        className={cn(
          "w-0.5 relative translate-x-[25px]",
          isLast ? "bg-transparent" : "bg-neutral-object-accent",
        )}
      >
        <span className="absolute left-3 top-[14px] w-2 h-2 bg-neutral-object-accent rounded-full"></span>
        <span className="absolute w-3 h-5 border-l-2 border-t-2 border-neutral-object-accent rotate-x-180 rounded-tl-md"></span>
      </div>

      <div className="relative pl-[50px] mb-4 w-full">
        <div className="py-1">
          <h3 className="mb-4 space-x-2">
            <span className="text-body-quiet">Paso {index}</span>
            <span className="text-body-loud">{form.name}</span>
          </h3>
          <div className="space-y-3">
            {form.sections
              .flatMap((section) => section.fields)
              .map((field, index) => {
                let type = parseType(field.type);
                if (type === "file") {
                  if (field?.multiple) {
                    type = "array";
                  }
                }
                if (field.type === "select") {
                  if (field?.variableRef?.type === "boolean") {
                    type = "boolean";
                  } else if (field?.variableRef?.type === "number") {
                    type = "number";
                  } else {
                    type = "string";
                  }
                }
                return (
                  <VariableKeyBox
                    name={field.technicalKey}
                    type={
                      type as "string" | "number" | "boolean" | "array" | "date"
                    }
                    key={`field-${field.technicalKey}-${index}`}
                  />
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};
